import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  tableRowSize,
  stickyHeader,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { Loader } from "components/Loader";
import { Typography, Box, Paper } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { CustomButton } from "components/CustomComponents";
import { showToast } from "services/utils/Status";
import { SalaryRevision } from "./SalaryRevision";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { useSalaryRevise } from "./SalaryReviseContext";

import {
  salaryRevisionGroupedHeader,
  salaryRevisionHeader,
} from "pages/HR/Headers";
import { customGetAPI } from "services/utils/CustomAPI";

export const SalaryRevise = () => {
  const { domain, topbarCompanyId, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );

  const containerRef = useRef(null);
  const [salaryRevisionDetails, setSalaryRevisionDetails] = useState([]);
  const [getRevisionId, setGetRevisionId] = useState("");
  const [trigger, setTrigger] = useState(false);
  const {
    setEditData,
    setPrefillDetails,
    showReprocessDialog,
    setShowReprocessDialog,
  } = useSalaryRevise();
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Salary Configuration",
    "Salary Revision",
  );

  const {
    result: revisionData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}hrzone/salary/salary_revision/salary-revision/?company_id=${topbarCompanyId}`,
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setSalaryRevisionDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
    }
  }, [reCallApi, topbarCompanyId]);

  const NewPayrunButton = (
    <CustomButton
      actionFuntion={() => {
        if (check_access) {
          setShowReprocessDialog(true);
        } else {
          showToast(
            "error",
            "Access denied: You are not authorized to create salary component",
          );
        }
      }}
      actionName="New"
      typeName="button"
    />
  );

  useEffect(() => {
    const getEditData = async () => {
      const res = await customGetAPI(
        `${domain}hrzone/salary/salary_revision/salary-revision/?revision_id=${getRevisionId}`,
      );
      if (res.ok) {
        setEditData(res?.responseDetails);
        setTrigger(false);
      } else {
        showToast("error", res?.message);
      }
    };
    if (trigger && getRevisionId) {
      getEditData();
    }
  }, [domain, getRevisionId, setEditData, trigger]);

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Salary Revise
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a predefined salary structure by grouping components.
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title={"Salary Revise Records"}
            filterHeaders={salaryRevisionHeader}
            data={revisionData}
            filteredData={salaryRevisionDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            newButtonComponent={NewPayrunButton}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {salaryRevisionGroupedHeader.map((header) => (
                      <Mui.TableCell
                        key={header.name}
                        align="center"
                        colSpan={header.children ? header.children.length : 1}
                        rowSpan={header.children ? 1 : 2}
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                        }}
                      >
                        {header.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                  <Mui.TableRow sx={tableRowSize}>
                    {salaryRevisionGroupedHeader
                      .filter((header) => header.children)
                      .flatMap((header) =>
                        header.children.map((child) => (
                          <Mui.TableCell
                            key={child.key}
                            align="center"
                            sx={{
                              color: themes.headLine,
                              fontWeight: "bold",
                              border: "1px solid #ddd",
                            }}
                          >
                            {child.name}
                          </Mui.TableCell>
                        )),
                      )}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {salaryRevisionDetails?.length === 0 ? (
                    <InfoMessage dataLength={revisionData} />
                  ) : (
                    salaryRevisionDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((value) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {salaryRevisionGroupedHeader.flatMap((header) =>
                            header.children
                              ? header.children.map((child) => (
                                  <Mui.TableCell
                                    key={child.key}
                                    align="center"
                                    sx={{ border: "1px solid #ddd" }}
                                    onClick={() => {
                                      setShowReprocessDialog(true);
                                      setGetRevisionId(value?.revision_id);
                                      setPrefillDetails(true);
                                      setTrigger(true);
                                    }}
                                  >
                                    {typeof value[child.key] === "boolean"
                                      ? value[child.key]
                                        ? "Enabled"
                                        : "Disabled"
                                      : (value[child.key] ?? "-")}
                                  </Mui.TableCell>
                                ))
                              : [
                                  <Mui.TableCell
                                    key={header.key}
                                    align="center"
                                    onClick={() => {
                                      setShowReprocessDialog(true);
                                      setGetRevisionId(value?.revision_id);
                                      setPrefillDetails(true);
                                      setTrigger(true);
                                    }}
                                    sx={{ border: "1px solid #ddd" }}
                                  >
                                    {typeof value[header.key] === "boolean"
                                      ? value[header.key]
                                        ? "Enabled"
                                        : "Disabled"
                                      : (value[header.key] ?? "-")}
                                  </Mui.TableCell>,
                                ],
                          )}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {salaryRevisionDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={salaryRevisionDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>
      {showReprocessDialog && <SalaryRevision reCallApi={reCallApi} />}
    </React.Fragment>
  );
};
